import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TextInput, StyleSheet, SafeAreaView, KeyboardAvoidingView, Platform, TouchableOpacity, Alert } from 'react-native';
import { useRouter } from 'expo-router';
import { Button } from '../components/Button';
import { Colors, SIZES } from '../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { useAppStore } from '../store/useAppStore';
import { mensajeDeError } from '../utils/mensajeDeError';

export default function VerifyCodeScreen() {
  const router = useRouter();
  const role = useAppStore((state) => state.userRole);
  
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [timer, setTimer] = useState(60);
  const inputs = useRef<Array<TextInput | null>>([]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    if (timer > 0) {
      interval = setInterval(() => setTimer(prev => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleChangeText = (text: string, index: number) => {
    const newCode = [...code];
    newCode[index] = text;
    setCode(newCode);

    // Auto-advance
    if (text.length === 1 && index < 5) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyPress = (e: any, index: number) => {
    if (e.nativeEvent.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  // Aviso plataforma — SMS solo está implementado en web (vía RecaptchaVerifier).
  // En móvil hace falta @react-native-firebase/auth phoneNumber + reCAPTCHA
  // Enterprise, que NO está configurado. Bloqueamos suavemente en vez de
  // dejar un "bypass" que aceptaba cualquier código (era un hueco de seguridad).
  const showInfo = (msg: string) => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.alert(msg);
    } else {
      Alert.alert('Verificación', msg);
    }
  };

  const handleVerify = async () => {
    const fullCode = code.join('');
    if (fullCode.length < 6) {
      showInfo('Por favor ingresa el código completo de 6 dígitos.');
      return;
    }

    if (Platform.OS !== 'web') {
      Alert.alert(
        'Verificación SMS no disponible',
        'En la app móvil aún no está habilitada la verificación por SMS. Te llevamos al registro con correo electrónico.'
      );
      router.replace('/auth');
      return;
    }

    setLoading(true);
    try {
      const confirmationResult = (window as any).confirmationResult;
      if (!confirmationResult) {
        throw new Error('Sesión de SMS expirada. Vuelve a solicitar el código.');
      }
      await confirmationResult.confirm(fullCode);

      setLoading(false);
      if (role === 'PASSENGER') {
        router.replace('/(tabs)/map');
      } else if (role === 'RENT_CAR_OWNER') {
        router.replace('/(tabs)/dashboard');
      } else if (role === 'CAR_RENTER') {
        router.replace('/(tabs)/marketplace');
      } else if (role === 'SCHOOL_DIRECTOR') {
        router.replace('/school-director-dashboard');
      } else if (role === 'DRIVER' || role === 'SCHOOL_DRIVER' || role === 'DELIVERY_DRIVER') {
        router.replace('/(tabs)/map');
      } else {
        router.replace('/profile-photo-capture');
      }
    } catch (error: any) {
      setLoading(false);
      showInfo(mensajeDeError(error, 'El código ingresado es incorrecto o expiró.'));
    }
  };

  const resendCode = () => {
    if (timer === 0) {
      setTimer(60);
      showInfo('Código reenviado a tu correo electrónico.');
    }
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.container}
      >
        <TouchableOpacity onPress={() => router.canGoBack() ? router.back() : router.replace('/')} style={styles.backButton}>
          <Ionicons name="arrow-back" size={24} color={Colors.white} />
        </TouchableOpacity>

        <View style={styles.header}>
          <Text style={styles.title}>Verifica tu cuenta</Text>
          <Text style={styles.subtitle}>
            Hemos enviado un código de 6 dígitos a tu correo electrónico. Por favor, ingrésalo a continuación.
          </Text>
        </View>

        <View style={styles.form}>
          <View style={styles.codeContainer}>
            {code.map((digit, index) => (
              <TextInput
                key={index}
                ref={(ref) => { inputs.current[index] = ref; }}
                style={styles.codeInput}
                keyboardType="number-pad"
                maxLength={1}
                value={digit}
                onChangeText={(text) => handleChangeText(text, index)}
                onKeyPress={(e) => handleKeyPress(e, index)}
                selectTextOnFocus
              />
            ))}
          </View>
          
          <Button 
            title="Verificar Cuenta" 
            onPress={handleVerify} 
            loading={loading}
            style={styles.button}
          />
          
          <View style={styles.resendContainer}>
            <Text style={styles.resendText}>¿No recibiste el código? </Text>
            <TouchableOpacity onPress={resendCode} disabled={timer > 0}>
              <Text style={[styles.resendLink, timer > 0 && styles.resendLinkDisabled]}>
                {timer > 0 ? `Reenviar en ${timer}s` : 'Reenviar Código'}
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: Colors.primary,
  },
  container: {
    flex: 1,
    justifyContent: 'space-between',
  },
  backButton: {
    padding: SIZES.padding,
    marginTop: Platform.OS === 'ios' ? 0 : 20,
  },
  header: {
    paddingHorizontal: SIZES.padding,
    paddingBottom: 40,
  },
  title: {
    fontSize: 32,
    fontWeight: 'bold',
    color: Colors.white,
    marginBottom: 12,
  },
  subtitle: {
    color: Colors.background,
    fontSize: SIZES.medium,
    lineHeight: 22,
    opacity: 0.9,
  },
  form: {
    flex: 1,
    backgroundColor: Colors.white,
    borderTopLeftRadius: 32,
    borderTopRightRadius: 32,
    padding: SIZES.padding * 1.5,
  },
  codeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 30,
    marginTop: 10,
  },
  codeInput: {
    width: 45,
    height: 55,
    borderWidth: 1.5,
    borderColor: Colors.border,
    borderRadius: SIZES.radius,
    fontSize: 24,
    fontWeight: 'bold',
    textAlign: 'center',
    color: Colors.text,
    backgroundColor: Colors.background,
  },
  button: {
    marginTop: 10,
  },
  resendContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    marginTop: 30,
  },
  resendText: {
    fontSize: 14,
    color: Colors.textSecondary,
  },
  resendLink: {
    fontSize: 14,
    color: Colors.primary,
    fontWeight: 'bold',
  },
  resendLinkDisabled: {
    color: '#A0A0A0',
  },
});

